/* ===========================================================
   TRACK ORDER — wired to useTrackOrder() with 10s polling.
   =========================================================== */
import React from "react";
import { useNavigate } from "react-router-dom";
import { ProductTile } from "@/components/product-card";
import { EmptyState } from "@/components/EmptyState";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { Breadcrumbs, getQuery } from "@/components/shell";
import { Icons } from "@/lib/icons";
import { useTrackOrder } from "@/api/hooks";

function TrackMap() {
  return (
    <svg viewBox="0 0 800 500" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#dbd9c8" strokeWidth="1"/>
        </pattern>
      </defs>
      <rect width="800" height="500" fill="url(#grid)"/>
      <path d="M -20 380 Q 200 200, 400 260 T 820 180" fill="none" stroke="#cfcdb8" strokeWidth="3"/>
      <path d="M 60 -20 Q 120 200, 260 260 T 540 460" fill="none" stroke="#cfcdb8" strokeWidth="3"/>
      <path d="M 820 60 Q 600 100, 540 240 T 240 460" fill="none" stroke="#cfcdb8" strokeWidth="3"/>
      <path d="M 80 380 C 220 350, 320 240, 420 260 S 640 320, 700 220"
            fill="none" stroke="var(--brand-700)" strokeWidth="4" strokeDasharray="0" strokeLinecap="round" />
      <path d="M 700 220 C 740 200, 760 180, 790 140"
            fill="none" stroke="var(--brand-700)" strokeWidth="4" strokeDasharray="2 8" strokeLinecap="round" opacity="0.5"/>
      <g transform="translate(80 380)">
        <circle r="14" fill="var(--ink)"/>
        <circle r="6" fill="#fff"/>
        <text y="38" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="11" fill="var(--ink-2)" fontWeight="500">BHIWANDI</text>
      </g>
      <g transform="translate(700 220)">
        <circle r="18" fill="var(--brand-700)" opacity="0.2">
          <animate attributeName="r" values="14;24;14" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.3;0.05;0.3" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle r="14" fill="var(--brand-700)"/>
        <path d="M -5 -2 L -1 3 L 6 -4" stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" transform="translate(0 0.5)"/>
        <text y="-26" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="11" fontWeight="600" fill="var(--brand-700)">RIDER · LIVE</text>
      </g>
      <g transform="translate(790 140)">
        <path d="M 0 -16 C -10 -16, -14 -8, -14 0 C -14 12, 0 26, 0 26 C 0 26, 14 12, 14 0 C 14 -8, 10 -16, 0 -16 Z" fill="var(--ink)"/>
        <circle cy="-2" r="4" fill="#fff"/>
        <text y="46" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="11" fill="var(--ink-2)" fontWeight="500">YOUR PHARMACY</text>
      </g>
    </svg>
  );
}

const STATUS_LABEL = {
  placed: "Order placed",
  packed: "Packed at warehouse",
  shipped: "Shipped",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

function TrackPage() {
  const navigate = useNavigate();
  const initial = getQuery("id") || getQuery("order") || "";
  const [orderInput, setOrderInput] = React.useState(initial);
  const { data: order, isLoading, error } = useTrackOrder(initial || null);

  const handleFind = () => {
    const id = orderInput.trim();
    if (!id) return;
    navigate("/pages/storefront/track-order.html?id=" + encodeURIComponent(id));
  };

  if (!initial) {
    return (
      <>
        <Breadcrumbs items={[{ label: "Track order" }]} />
        <div className="container" style={{ padding: "32px 0" }}>
          <header style={{ paddingBottom: 16 }}>
            <h1 style={{ fontSize: "clamp(28px, 3.6vw, 40px)", letterSpacing: "-0.025em" }}>Track your order</h1>
            <p style={{ color: "var(--ink-3)", fontSize: 15, marginTop: 8 }}>
              Enter an order ID below or use the link from your confirmation email.
            </p>
          </header>
          <div className="track-find">
            <Icons.Search size={16} stroke={1.7} />
            <input
              className="input"
              placeholder="Order ID · NS-2026-NNNNN"
              value={orderInput}
              onChange={(e) => setOrderInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleFind(); }}
              style={{ border: "none" }}
            />
            <button className="btn btn-primary btn-sm" onClick={handleFind}>
              Find <Icons.ArrowR size={13} stroke={2} />
            </button>
          </div>
          <div style={{ marginTop: 32 }}>
            <EmptyState
              title="No order to track yet"
              description="Place an order to start tracking it here."
              cta={{ label: "Browse products", href: "/pages/storefront/products.html" }}
            />
          </div>
        </div>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <Breadcrumbs items={[{ label: "Track order" }]} />
        <div className="container" style={{ padding: "32px 0" }}>
          <LoadingSkeleton rows={6} height={48} />
        </div>
      </>
    );
  }

  if (error || !order) {
    return (
      <>
        <Breadcrumbs items={[{ label: "Track order" }]} />
        <div className="container" style={{ padding: "32px 0" }}>
          <div className="track-find" style={{ marginBottom: 24 }}>
            <Icons.Search size={16} stroke={1.7} />
            <input
              className="input"
              placeholder="Order ID · NS-2026-NNNNN"
              value={orderInput}
              onChange={(e) => setOrderInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleFind(); }}
              style={{ border: "none" }}
            />
            <button className="btn btn-primary btn-sm" onClick={handleFind}>
              Find <Icons.ArrowR size={13} stroke={2} />
            </button>
          </div>
          <EmptyState
            title="Order not found"
            description={`We couldn't find order ${initial}. Check the ID and try again.`}
          />
        </div>
      </>
    );
  }

  const stepCount = order.timeline.length;
  const doneCount = order.timeline.filter((t) => t.done).length;
  const isCancelled = order.status === "cancelled";

  return (
    <>
      <Breadcrumbs items={[{ label: "Track order" }]} />
      <div className="container">
        <header style={{ padding: "32px 0 8px" }}>
          <h1 style={{ fontSize: "clamp(28px, 3.6vw, 40px)", letterSpacing: "-0.025em" }}>Track your order</h1>
          <p style={{ color: "var(--ink-3)", fontSize: 15, marginTop: 8 }}>
            Status refreshes automatically. Enter a different ID to switch.
          </p>
        </header>

        <div className="track-find">
          <Icons.Search size={16} stroke={1.7} />
          <input
            className="input"
            placeholder="Order ID · NS-2026-NNNNN"
            value={orderInput}
            onChange={(e) => setOrderInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleFind(); }}
            style={{ border: "none" }}
          />
          <button className="btn btn-primary btn-sm" onClick={handleFind}>
            Find <Icons.ArrowR size={13} stroke={2} />
          </button>
        </div>

        <div className="track-layout">
          <div>
            <div className="track-status-card reveal">
              <div className="track-status-eyebrow">
                <span className="dot" />
                LIVE · {order.number}
              </div>
              <div className="track-status-title">{STATUS_LABEL[order.status] || order.status}</div>
              <div className="track-status-eta">{order.eta} · Rider {order.courier.rider}</div>
              <div className="track-status-meta">
                <div>
                  <div className="track-status-meta-k">PROGRESS</div>
                  <div className="track-status-meta-v">{doneCount} / {stepCount} stages</div>
                </div>
                <div>
                  <div className="track-status-meta-k">CARRIER</div>
                  <div className="track-status-meta-v">{order.courier.name}</div>
                </div>
                <div>
                  <div className="track-status-meta-k">AWB</div>
                  <div className="track-status-meta-v mono">{order.courier.awb}</div>
                </div>
              </div>
              <div className="track-progress-strip">
                {order.timeline.map((t, i) => (
                  <div key={i} className={"track-progress-step" + (t.done ? " is-done" : (t.code === order.status ? " is-current" : ""))} />
                ))}
              </div>
            </div>

            {!isCancelled && (
              <div className="track-map reveal" style={{ marginTop: 16 }}>
                <TrackMap />
              </div>
            )}

            <div className="confirm-card reveal" style={{ marginTop: 16 }}>
              <div className="confirm-card-h">Order journey</div>
              <div className="confirm-timeline">
                {order.timeline.map((t, i) => (
                  <div key={i} className="confirm-tl-row">
                    <div className="confirm-tl-marker">
                      <div className={"confirm-tl-dot" + (t.done ? " is-done" : (t.code === order.status ? " is-current" : ""))} />
                      {i < order.timeline.length - 1 && <div className="confirm-tl-line" />}
                    </div>
                    <div className="confirm-tl-body">
                      <div className="confirm-tl-label">{t.label}</div>
                      <div className="confirm-tl-meta">
                        {new Date(t.ts).toLocaleString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        {t.note ? " · " + t.note : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="track-courier reveal">
              <div className="confirm-card-h">Courier</div>
              <div className="track-courier-row">
                <div className="track-courier-row-ic"><Icons.Truck size={18} stroke={1.6} /></div>
                <div>
                  <div className="track-courier-row-k">CARRIER</div>
                  <div className="track-courier-row-v">{order.courier.name}</div>
                </div>
              </div>
              <div className="track-courier-row">
                <div className="track-courier-row-ic"><Icons.User size={18} stroke={1.6} /></div>
                <div>
                  <div className="track-courier-row-k">RIDER</div>
                  <div className="track-courier-row-v">{order.courier.rider}</div>
                </div>
              </div>
              <div className="track-courier-row">
                <div className="track-courier-row-ic"><Icons.Phone size={18} stroke={1.6} /></div>
                <div>
                  <div className="track-courier-row-k">CONTACT</div>
                  <div className="track-courier-row-v mono" style={{ fontSize: 13 }}>{order.courier.rider_phone}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <a href={"tel:" + (order.courier.rider_phone || "").replace(/\s/g, "")} className="btn btn-primary btn-sm" style={{ flex: 1, justifyContent: "center" }}>
                  <Icons.Phone size={13} stroke={2} /> Call rider
                </a>
                <button className="btn btn-outline btn-sm" style={{ flex: 1, justifyContent: "center" }}>
                  <Icons.Mail size={13} stroke={2} /> Message
                </button>
              </div>
            </div>

            <div className="confirm-card reveal" data-delay="1">
              <div className="confirm-card-h">Items</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {order.items.map((it) => (
                  <div key={it.product_slug} style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <div style={{ width: 40, height: 40 }}>
                      <ProductTile product={{ name: it.product_name, slug: it.product_slug }} size="sm" />
                    </div>
                    <div style={{ flex: 1, fontSize: 13 }}>
                      <div style={{ fontWeight: 500 }}>{it.product_name}</div>
                      <div style={{ color: "var(--ink-3)", fontSize: 12 }}>Qty {it.qty}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="confirm-card reveal" data-delay="2">
              <div className="confirm-card-h">Need help?</div>
              <div style={{ fontSize: 13, color: "var(--ink-3)", lineHeight: 1.6 }}>
                Anything wrong with the delivery? Open a ticket and a support specialist will reach out within 2 hours.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                <button className="btn btn-outline btn-sm" style={{ justifyContent: "flex-start" }}><Icons.Help size={13} stroke={2} /> Report an issue</button>
                <a href="/pages/policy/refund.html" className="btn btn-outline btn-sm" style={{ justifyContent: "flex-start" }}><Icons.ExtLink size={13} stroke={2} /> Refund policy</a>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}

export { TrackPage };
